import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import { getSuWidgetConfig } from "../../services/channelManager.js";

const ELEMENT_ID = "su-reviews-root";
const PMS_DOMAIN = "revivotech.in";

// Reuse the same singleton script-load promise as SuApiChannelMapper.
// The external script only needs to be fetched once per page session.
let suScriptPromise = null;

function loadSuWidgetScript() {
  if (typeof window !== "undefined" && window.loadScript) {
    return Promise.resolve();
  }
  if (suScriptPromise) {
    return suScriptPromise;
  }
  suScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://static.otaswitch.com/JS/script.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = (err) => reject(err);
    document.body.appendChild(script);
  });
  return suScriptPromise;
}

const SuReviewsWidget = () => {
  const { hotelDetails } = useSelector((store) => store.login);
  const hotelId = hotelDetails?.id;
  const isSuApiHotel = hotelDetails?.channelManagerName === "suapi";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Only initialise for SuAPI hotels
    if (!hotelId || !isSuApiHotel) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function init() {
      try {
        setError(null);
        setLoading(true);

        // 1. Load SU external script (shared singleton)
        await loadSuWidgetScript();
        if (cancelled) return;

        // 2. Fetch token + property id — same endpoint used by Channel Settings
        const data = await getSuWidgetConfig(hotelId);
        if (!data) throw new Error("No data returned from token API");

        const { token_id, proppmsid } = data;
        if (!token_id || !proppmsid) {
          throw new Error("Missing token_id or proppmsid from token API");
        }

        // 3. appId is the base64-encoded PMS domain (whitelisted by Su team)
        const appId = "emVuaWFwcGxpZWRzY2llbmNlc3B2dGx0ZC5zdWlzc3UuY29t";

        if (cancelled) return;

        // 4. Build the widget config — key difference: type is "review"
        const widgetConfig = {
          elementId: ELEMENT_ID,
          type: "review",         // <-- "review" instead of "channel-Mapping"
          appId,
          propertyId: proppmsid,
          token: token_id,
          language: "en",
          themeColor: {
            iconColor: "#1e8ce4",
          },
        };

        // 5. Mount the widget
        if (typeof window.loadScript === "function") {
          window.loadScript(widgetConfig);
        } else {
          throw new Error("SU widget loader (window.loadScript) not available");
        }

        if (!cancelled) setLoading(false);
      } catch (err) {
        console.error("[SuReviewsWidget]", err);
        if (!cancelled) {
          setError(err.message || "Failed to load Reviews widget");
          setLoading(false);
        }
      }
    }

    init();

    return () => {
      cancelled = true;
      const el = document.getElementById(ELEMENT_ID);
      if (el) el.innerHTML = "";
    };
  }, [hotelId, isSuApiHotel]);

  // ── Not a SuAPI hotel ──────────────────────────────────────────────────────
  if (!isSuApiHotel) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="pb-[120px] text-center">
          <p className="text-gray-500 font-nunito text-sm">
            Reviews are available for hotels connected via SuAPI channel manager.
          </p>
          <p className="text-gray-400 font-nunito text-sm mt-1">
            Please contact{" "}
            <span className="text-[#0000EE]">support@revivotech.in</span> to
            enable this.
          </p>
        </div>
      </div>
    );
  }

  // ── Widget (always render the mount point so loadScript finds the element) ──
  // Loading spinner and error banner render above the div, not instead of it.
  // This mirrors SuApiChannelMapper where #su-channel-mapping-root is always
  // in the DOM when window.loadScript is called.
  return (
    <div className="w-full h-full flex flex-col">
      {loading && !error && (
        <div className="flex items-center justify-center py-[40px]">
          <CircularProgress size={60} style={{ color: "#007FFF" }} />
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "12px 16px",
            marginBottom: "12px",
            borderRadius: 8,
            border: "1px solid #f87171",
            background: "#fef2f2",
            color: "#991b1b",
            fontSize: 14,
          }}
        >
          Failed to load Reviews: {error}
        </div>
      )}

      {/* SU widget mounts here — must always be in the DOM when loadScript runs */}
      <div
        id={ELEMENT_ID}
        style={{ width: "100%", minHeight: "80vh" }}
        data-testid="su-reviews-widget-root"
      />
    </div>
  );
};

export default SuReviewsWidget;
